import SendIcon from '@mui/icons-material/Send';
import {Button, Grid} from '@mui/material';
import {makeStyles} from '@mui/styles';
import {Box} from '@mui/system';
import PropTypes from 'prop-types';
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {FormProvider, useForm, useWatch} from 'react-hook-form';
import {useSelector} from 'react-redux';
import base_url from '../../../../baseUrls';
import * as allConstants from '../../../../constants/allConstants';
import {NewPermissionGate} from '../../../../permissions/permissionGate';
import CustomFormBlock from '../../../../pieces/inputs/formBlock';
import {NoDataStringWrapper} from '../../../../pieces/noDataStringWrapper';
import ApiUtils from '../../../../utils/apiUtils';
import Utils from '../../../../utils/utils';
import Loader from '../../../misc/loader';
import {contractorJobRules, CONTRACTOR_JOB_ELEMENTS} from '../../../../permissions/jobsAccessControl';
import {useJobDetailViewStyles} from '../views/jobDetailViewStyles';
import MessageView from '../views/messageView';
import NoteType from '../views/selectNoteTypeView';
import {useJob} from '../../../../contextProviders/jobContext';

const NOTE_TEXT_MAX_LENGTH = 2000;

const useStyles = makeStyles((theme) => ({
  note_input: {
    '& .MuiInputBase-inputMultiline': {
      width: '100% !important',
    },
    '& .MuiFormControl-root': {
      marginBottom: 0,
      '& textarea': {
        margin: 0,
      }
    },
    '& .MuiFormHelperText-root': {
      fontSize: '14px',
      lineHeight: 1.35,
      minHeight: 'auto',
      alignSelf: 'flex-start',
    },
  },
}));

function Notes(props) {

  const jobContext = useJob();

  const formOptions = {
    mode: 'onChange',
    reValidateMode: 'onChange',
    criteriaMode:'all',
  };

  const jobType = props[allConstants.JOB_FIELDS.JOB_TYPE.api_name];
  const company = props.company;
  const allowedTests = Array.isArray(company?.[allConstants.COMPANY_FIELDS.ALLOWED_TESTS.api_name])
    ? company[allConstants.COMPANY_FIELDS.ALLOWED_TESTS.api_name]
    : [];
  const allowedPermits = Array.isArray(company?.[allConstants.COMPANY_FIELDS.ALLOWED_PERMITS.api_name])
    ? company[allConstants.COMPANY_FIELDS.ALLOWED_PERMITS.api_name]
    : [];
  const hasCompanyScope = !!company?.id;
  const hasHersService = !hasCompanyScope || allowedTests.includes(allConstants.TEST_TYPES.HERS.value);
  const hasPermitService = !hasCompanyScope || allowedPermits.includes(jobType);

  let defaultNoteType = allConstants.NOTE_TYPES.PERMIT.value;

  if(jobType === allConstants.JOB_TYPES.TESTING_ONLY.value) {
    defaultNoteType = allConstants.NOTE_TYPES.HERS.value;
  }

  const canSelectNoteType = jobType !== allConstants.JOB_TYPES.BUSINESS_LIC.value;

  const messagesEndRef = useRef(null);
  const formMethods = useForm(formOptions);

  const noteFieldName = allConstants.JOB_FIELDS.NOTE.TEXT.api_name;

  const watchNote = useWatch({
    control: formMethods.control,
    name: noteFieldName,
  });

  const onNoteBlurExtra = useCallback((e) => {
    const val = e.target.value ?? '';
    if (val.length > NOTE_TEXT_MAX_LENGTH) {
      formMethods.setError(noteFieldName, {
        type: 'maxLength',
        message: `Note must be ${NOTE_TEXT_MAX_LENGTH} characters or fewer.`,
      });
    } else {
      const err = formMethods.getFieldState(noteFieldName, formMethods.formState).error;
      if (err?.type === 'maxLength') {
        formMethods.clearErrors(noteFieldName);
      }
    }
  }, [formMethods, noteFieldName]);

  useEffect(() => {
    if (!watchNote || watchNote.length <= NOTE_TEXT_MAX_LENGTH) {
      const err = formMethods.formState.errors[noteFieldName];
      if (err?.type === 'maxLength') {
        formMethods.clearErrors(noteFieldName);
      }
    }
  }, [watchNote, formMethods, noteFieldName]);

  if(Utils.isDevEnv()) {
    window.noteForm = formMethods;
  }
  const classes = {...useStyles(), ...useJobDetailViewStyles()};
  const [showNoteSwitch, setShowNoteSwitch] = useState(false);

  const [noteType, setNoteType] = useState(defaultNoteType);
  const [fetching, setFetching] = useState(false);
  const user = useSelector(state => state.user);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({behavior: 'smooth'});
  };

  const messages = props.jobNotes;
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const openNoteSwitch = () => {
    setShowNoteSwitch(true);
  };

  const closeNoteSwitch = () => {
    setShowNoteSwitch(false);
  };

  const notesInputDefaultProps = {
    viewMode: false,
    renderLabel: true,
  };

  const makeNoteObject = (noteData) => {
    const timeCreate = new Date(Date.now()).toISOString();
    const noteObject = {
      id: noteData.jobId,
      note: noteData.note,
      type: noteData.type,
      createdAt: timeCreate,
      author: {
        firstName: user.userName.firstName,
        lastName: user.userName.lastName,
        role: allConstants.CONTRACTOR_DISPLAY_ROLES.CONTRACTOR.value,
      },
    };

    return noteObject;
  };

  const onSubmit = async (data) => {
    const currentNoteOptions = getNoteOptions();
    if (currentNoteOptions.length === 0) {
      return;
    }

    const rawNote = data[noteFieldName] ?? '';
    if (!rawNote.trim() || rawNote.length > NOTE_TEXT_MAX_LENGTH) {
      return;
    }

    closeNoteSwitch();
    setFetching(true);

    const touchedFields = formMethods.formState.touchedFields;
    const cleanData = Utils.dirtyValues(touchedFields, data);
    const authToken = await ApiUtils.getAccessToken();

    if(!authToken) {
      return;
    }

    const noteData = {
      jobId: props.id,
      note: cleanData.note,
      type: noteType,
    };

    const response = await fetch(`${base_url.api}jobs/add-note`,
      {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: authToken,
        },
        body: JSON.stringify(
          noteData
        ),
      }).catch((error) => {
      setFetching(false);
      console.log(error);
    });

    const responseJson = await response.json();

    Utils.handleToastMessage(responseJson, 'Note added', 'Please try adding note again', 'add_note');

    if (responseJson.status === 'success') {
      formMethods.reset();
      const note = makeNoteObject(noteData);
      props.addNote(note);
    }
    setNoteType(getDefaultNoteType(currentNoteOptions));
    setFetching(false);
  };

  const getNotes = ()=> {
    let notes;

    if(props.jobNotes && props.jobNotes.length !== 0) {
      const notesCount = props.jobNotes.length;
      const jobNotes = props.jobNotes;
      notes = jobNotes.map((note, idx) => {
        return <MessageView key={note.id}
          firstName={note.author[allConstants.USER_FIELDS.FIRST_NAME.api_name]}
          isLastChild={idx+1=== notesCount}
          lastName={note.author[allConstants.USER_FIELDS.LAST_NAME.api_name]}
          date={note.createdAt}
          message={note.note}
          role={note.author.role}/>;
      });
    } else {
      notes =  <Box sx={{height: 'calc(48vh - 16px)'}}><NoDataStringWrapper text={'No notes added'}/></Box>;
    }
    if(!notes || notes.length === 0) return  null;
    return (
      <>
        <Box>
          {notes}
        </Box>
      </>
    );
  };

  const getNoteOptions = () => {
    const noteOptions = [];
    if(jobContext.isTestingOnly()) {
      if (hasHersService) {
        noteOptions.push({...allConstants.NOTE_TYPES.SCHEDULING, display: 'scheduling hers test'});
        noteOptions.push({...allConstants.NOTE_TYPES.HERS, default: true});
      }

    } else {
      if (hasPermitService) {
        noteOptions.push({...allConstants.NOTE_TYPES.PERMIT, default: true});
      }
      if(jobContext.hersIsRequired() && hasHersService) {
        noteOptions.push({...allConstants.NOTE_TYPES.SCHEDULING, display: 'scheduling hers test'});
        noteOptions.push({ ...allConstants.NOTE_TYPES.HERS });
      }
    }

    if (!jobContext.isBusinessLicense() && jobContext.jobHasInspection()) {
      noteOptions.push({ ...allConstants.NOTE_TYPES.INSPECTION, display: 'final city inspection' });
    }

    // A job outside the company's allowedPermits/allowedTests (e.g. a Plumbing job on a
    // Misc-only company) matches none of the above, and an empty list disables Send.
    // The job still belongs to its own department, so offer that note type.
    if (noteOptions.length === 0) {
      const fallbackType = jobContext.isTestingOnly() ? allConstants.NOTE_TYPES.HERS : allConstants.NOTE_TYPES.PERMIT;
      noteOptions.push({...fallbackType, default: true});
    }

    return noteOptions;
  };

  function getDefaultNoteType(options = []) {
    const defaultOption = options.find((option) => option.default);
    if (defaultOption) {
      return defaultOption.value;
    }
    return options[0]?.value || defaultNoteType;
  }

  const noteTypeOptions = getNoteOptions();

  useEffect(() => {
    const noteTypeAvailable = noteTypeOptions.some((option) => option.value === noteType);
    if (!noteTypeAvailable && noteTypeOptions.length > 0) {
      setNoteType(getDefaultNoteType(noteTypeOptions));
    }
  }, [noteType, noteTypeOptions]);

  const notes = getNotes();
  return (
    <Box display={'flex'} flexDirection={'column'} width={'100%'}>
      <Grid container xs={12}>
        <Grid item xs={12}>
          <Box className={classes.scrollable_tab_content} display={'flex'} sx={{width: '100%'}}>
            <Box sx={{
              width: notes ? '95%' : '100%',
              // height: 'inherit',
            }}>
              {
                notes && <>
                  {notes}
                  <Box ref={messagesEndRef}/>
                </>

              }
            </Box>
          </Box>
        </Grid>
      </Grid>
      <Grid container xs={12}>
        { fetching && <Loader/> }
        <Grid item xs={12}>
          <FormProvider {...formMethods}>
            <form onSubmit={formMethods.handleSubmit(onSubmit)} method={'POST'}>
              {showNoteSwitch && <NoteType
                open={true}
                onClose={closeNoteSwitch}
                onToggle={(type) => {
                  setNoteType(type);
                }}
                noteTypeOptions={noteTypeOptions}
                onSubmit={formMethods.handleSubmit(onSubmit)}/>
              }
              <Box
                display={'flex'}
                flexDirection={'column'}
                gap={2}
                sx={{
                  paddingBottom: 0,
                  '& .scrollable_tab_content': {
                    height: '40vh'
                  }
                }}
              >
                <NewPermissionGate user={user}
                  elementPermissions={contractorJobRules[CONTRACTOR_JOB_ELEMENTS.addNote]}>
                  <Box
                    display={'flex'}
                    flexDirection={'row'}
                    alignItems={'center'}
                    gap={3}
                    sx={{
                      // width: 'calc(100% - 60px)',
                      // paddingBottom: '24px',
                      backgroundColor: '#fff'
                    }}
                    className={classes.note_input}
                  >
                    <CustomFormBlock
                      {...notesInputDefaultProps}
                      multiline
                      fullWidth
                      rowCount={1}
                      isEditable={!fetching}
                      field={allConstants.JOB_FIELDS.NOTE.TEXT}
                      renderLabel={false}
                      hideHelper={false}
                      onBlurExtra={onNoteBlurExtra}
                      maxWidth={'100%'}
                      placeholder={'Enter your message here'}
                    />
                    <Button
                      variant='contained'
                      disabled={
                        fetching
                        || noteTypeOptions.length === 0
                        || !watchNote
                        || watchNote.trim().length === 0
                        || watchNote.length > NOTE_TEXT_MAX_LENGTH
                      }
                      // onClick={()=> {console.log('clikc')}}
                      type={canSelectNoteType ? 'button': 'submit'}
                      onClick={canSelectNoteType ? openNoteSwitch: null}
                    >
                      <SendIcon/>
                    </Button>
                  </Box>
                </NewPermissionGate>
              </Box>
            </form>
          </FormProvider>
        </Grid>
      </Grid>
    </Box>
  );
}

Notes.propTypes = {
  id: PropTypes.string,
  jobNotes: PropTypes.instanceOf(Object).isRequired,
  addNote: PropTypes.func,
  isHERSRequired: PropTypes.bool,
};

export default Notes;
